import { Platform } from './Platform';
import { Color } from './Color';
import { GameOutcome } from './GameOutcome';

export type GamesRequestParams = Readonly<{
  username?: string | null;
  color?: Color | null;
  result?: GameOutcome | null;
  platform?: Platform | null;
  timeControl?: string | null;
  opening?: string | null;
  from?: string | null;
  to?: string | null;
  // Rating range (either player) and pattern (a CommentaryTheme, deep-linked from /patterns).
  ratingFrom?: number | null;
  ratingTo?: number | null;
  pattern?: string | null;
  // Time-control speed category, by SpeedClass name. Round-trips through the URL for /patterns
  // deep links.
  speed?: string | null;
  // Repair standing: 'never' (nothing in the game repaired) or 'stale' (nothing in the last 30 days,
  // including never). Round-trips through the URL.
  repair?: string | null;
  pgn?: string | null;
  onlyStarred?: boolean | null;
  favoriteMoves?: boolean | null;
  hasComments?: boolean | null;
  commentedByMe?: boolean | null;
  page?: number | null;
  // The group, by its 8-char URL token (the numeric id stays out of links). Null = the public Home group.
  group?: string | null;
}>;

export function getGamesUrl(params: GamesRequestParams, gameId?: number | null) {
  const {
    username,
    color,
    result,
    platform,
    opening,
    timeControl,
    from,
    to,
    ratingFrom,
    ratingTo,
    pattern,
    speed,
    repair,
    pgn,
    onlyStarred,
    favoriteMoves,
    hasComments,
    commentedByMe,
    page,
    group,
  } = params;
  const query = new URLSearchParams();
  if (username != null) {
    query.append('username', username);
  }
  if (color != null) {
    query.append('color', Color[color]);
  }
  if (result != null) {
    query.append('result', GameOutcome[result]);
  }
  if (platform != null) {
    query.append('platform', Platform[platform]);
  }
  if (opening != null) {
    query.append('opening', opening);
  }
  if (timeControl != null) {
    query.append('timeControl', timeControl);
  }
  if (from != null) {
    query.append('from', from);
  }
  if (to != null) {
    query.append('to', to);
  }
  if (ratingFrom != null) {
    query.append('ratingFrom', ratingFrom.toString());
  }
  if (ratingTo != null) {
    query.append('ratingTo', ratingTo.toString());
  }
  if (pattern != null) {
    query.append('pattern', pattern);
  }
  if (speed != null) {
    query.append('speed', speed);
  }
  if (repair != null) {
    query.append('repair', repair);
  }
  if (pgn != null) {
    query.append('pgn', pgn);
  }
  if (onlyStarred) {
    query.append('onlyStarred', 'true');
  }
  if (favoriteMoves) {
    query.append('favoriteMoves', 'true');
  }
  if (hasComments) {
    query.append('hasComments', 'true');
  }
  if (commentedByMe) {
    query.append('commentedByMe', 'true');
  }
  if (page != null && page > 0) {
    query.append('page', page.toString());
  }
  if (group != null) {
    query.append('group', group);
  }
  if (gameId) {
    query.append('gameId', gameId.toString());
  }
  // query.size isn't supported on iPad/iPhone.
  if ([...query].length > 0) {
    return `/?${query.toString()}`;
  }
  return '/';
}

// The inverse of getGamesUrl. Empty values (`to=`) count as not set.
export function parseGamesQuery(query: URLSearchParams): GamesRequestParams {
  const val = (key: string) => {
    const v = query.get(key);
    return v != null && v !== '' ? v : null;
  };
  const int = (key: string) => {
    const v = val(key);
    const n = v != null ? parseInt(v, 10) : NaN;
    return isNaN(n) ? null : n;
  };
  const color = val('color');
  const result = val('result');
  const platform = val('platform');
  return {
    username: val('username'),
    group: val('group'),
    color: color != null ? Color[color as keyof typeof Color] : null,
    result: result != null ? GameOutcome[result as keyof typeof GameOutcome] : null,
    platform: platform != null ? Platform[platform as keyof typeof Platform] : null,
    opening: val('opening'),
    timeControl: val('timeControl'),
    from: val('from'),
    to: val('to'),
    ratingFrom: int('ratingFrom'),
    ratingTo: int('ratingTo'),
    pattern: val('pattern'),
    speed: val('speed'),
    repair: val('repair'),
    pgn: val('pgn'),
    onlyStarred: val('onlyStarred') != null ? true : null,
    favoriteMoves: val('favoriteMoves') != null ? true : null,
    hasComments: val('hasComments') != null ? true : null,
    commentedByMe: val('commentedByMe') != null ? true : null,
    page: Math.max(0, int('page') ?? 0),
  };
}
